import type { DateRange, FilterType } from '@hotjar/global-filters';
import { FilterKindEnum } from '@hotjar/global-filters';
import type { PlanComparisonReferrer } from '@hotjar/webapp/features/Billing/PlanComparisonModal/types';
import type { AdditionalTrackingData, StepSeries } from '@hotjar/dashboard-quantz';
import { useTranslation } from '@hotjar/translations';
import { StatefulStep } from '@hotjar/webapp/features/FunnelsStep/StatefulComponent/StatefulStep';

import { ProductType } from '@hotjar/webapp/domains/common/product/types';
import {
  MAX_DATA_RETENTION_PERIOD,
  START_DATE_OF_QUANTZ_DATA_COLLECTION,
} from '@hotjar/webapp/features/GlobalFilterChain/StatefulComponent/StatefulFilterChain';
import { Steps } from '../../TrendsPage/Steps';
import { DeleteButton } from '../../TrendsPage/DeleteButton';

const SUPPORTED_FILTER_LIST: Set<FilterKindEnum> = new Set([
  FilterKindEnum.Clicked,
  FilterKindEnum.VisitedURL,
  FilterKindEnum.Event,
]);

interface FunnelStepWrapperProps {
  stepSeries: StepSeries;
  dateRange?: DateRange;
  isDeletable: boolean;
  isFirstChain: boolean;
  isLastChain: boolean;
  isFiltersDropdownVisibleByDefault: boolean;
  index: number;
  isInvalid: boolean;
  onStepChange: (steps: FilterType[], dateRange?: DateRange) => void;
  onUpsellRequest: (
    featureName: PlanComparisonReferrer,
    additionalTrackingData?: {
      // eslint-disable-next-line camelcase
      ui_position: string;
    },
  ) => void;
  onChainDelete: () => void;
  trackClick: (eventName: string, additionalTrackingData: AdditionalTrackingData) => void;
}

export const FunnelStepWrapper = ({
  stepSeries,
  dateRange,
  isDeletable,
  isFirstChain,
  isLastChain,
  isFiltersDropdownVisibleByDefault,
  index,
  isInvalid,
  onStepChange,
  onUpsellRequest,
  onChainDelete,
  trackClick,
}: FunnelStepWrapperProps): JSX.Element => {
  const { t } = useTranslation('funnels');

  return (
    <Steps.Step
      isFirstChain={isFirstChain}
      isLastChain={isLastChain}
      isInvalid={isInvalid}
      errorText={t`stepError`}
      stepMarker={{ kind: 'text', textValue: `${index + 1}` }}
    >
      <StatefulStep
        currentStepInUiFormat={{
          dateRange,
          filterList: stepSeries.stepInUiFormat,
        }}
        isFiltersDropdownVisibleByDefault={isFiltersDropdownVisibleByDefault}
        autoFocusAddFilter={isFirstChain}
        onChange={(filterInUiFormat) =>
          onStepChange(filterInUiFormat.filterList, filterInUiFormat.dateRange)
        }
        onUpsellRequest={onUpsellRequest}
        supportedFilterList={SUPPORTED_FILTER_LIST}
        dateConstraint={
          MAX_DATA_RETENTION_PERIOD.minDate < START_DATE_OF_QUANTZ_DATA_COLLECTION.minDate
            ? START_DATE_OF_QUANTZ_DATA_COLLECTION
            : MAX_DATA_RETENTION_PERIOD
        }
        contextOfUse={{
          productType: ProductType.Analytics,
          page: 'funnels',
          toolName: 'Funnels',
        }}
      />
      {isDeletable && (
        <DeleteButton
          onClick={() => {
            trackClick('cleared_step', {
              ui_position: 'funnels',
              state_event_was_triggered_on: 'funnels',
              element_name: 'cleared_step',
            });
            onChainDelete();
          }}
          tooltipText={t('removeStep')}
        />
      )}
    </Steps.Step>
  );
};
